/* global customElements, HTMLElement */
if (!customElements.get('interactive-reveal')) {
  customElements.define(
    'interactive-reveal',
    class InteractiveReveal extends HTMLElement {
      connectedCallback() {
        this._analyticsEnabled = this.dataset.analytics === 'true';
        this._isDesignMode = this.dataset.designMode === 'true';
        this._abortController = new AbortController();

        this._mediaSlides = Array.from(this.querySelectorAll('[data-ir-media-strip] > .interactive-reveal__slide'));
        this._contentSlides = Array.from(this.querySelectorAll('[data-ir-content-strip] > .interactive-reveal__slide'));
        this._thumbs = Array.from(this.querySelectorAll('.interactive-reveal__thumb'));
        this._thumbItems = Array.from(this.querySelectorAll('.interactive-reveal__thumb-item'));
        this._prevBtn = this.querySelector('[data-ir-prev]');
        this._nextBtn = this.querySelector('[data-ir-next]');
        this._liveRegion = this.querySelector('[data-ir-live]');
        this._positionTmpl = this.dataset.slidePositionTmpl || 'Slide __index__ of __total__: __title__';

        this._total = this._thumbs.length;
        this._activeIndex = 0;

        if (this._total < 2) return;

        this._mediaFrame = this.querySelector('.interactive-reveal__media-frame');

        this._bindEvents();
        this._bindSwipe();
        this._refresh({ announce: false });
      }

      disconnectedCallback() {
        this._abortController?.abort();
      }

      _bindEvents() {
        const { signal } = this._abortController;

        this._thumbs.forEach((thumb, index) => {
          thumb.addEventListener(
            'click',
            (e) => {
              e.preventDefault();
              this.setActive(index);
            },
            { signal }
          );

          thumb.addEventListener(
            'keydown',
            (e) => this._handleThumbKeydown(e, index),
            { signal }
          );
        });

        this._prevBtn?.addEventListener(
          'click',
          () => this.setActive(this._activeIndex - 1),
          { signal }
        );

        this._nextBtn?.addEventListener(
          'click',
          () => this.setActive(this._activeIndex + 1),
          { signal }
        );
      }

      _handleThumbKeydown(e, index) {
        let nextIndex = null;
        switch (e.key) {
          case 'ArrowRight':
          case 'ArrowDown':
            nextIndex = (index + 1) % this._total;
            break;
          case 'ArrowLeft':
          case 'ArrowUp':
            nextIndex = (index - 1 + this._total) % this._total;
            break;
          case 'Home':
            nextIndex = 0;
            break;
          case 'End':
            nextIndex = this._total - 1;
            break;
          default:
            return;
        }
        e.preventDefault();
        this.setActive(nextIndex);
        this._thumbs[nextIndex].focus();
      }

      setActive(index) {
        if (index < 0 || index >= this._total) return;
        if (index === this._activeIndex && this._mediaSlides[index]?.getAttribute('aria-hidden') !== 'true') {
          return;
        }
        const previousIndex = this._activeIndex;
        this._activeIndex = index;
        this._withThumbFlip(() => this._refresh({ announce: true, previousIndex }));
        this._dispatchAnalytics(index);
      }

      _thumbFlipEligible() {
        return (
          typeof this.animate === 'function' &&
          window.matchMedia('(max-width: 989px)').matches &&
          !window.matchMedia('(prefers-reduced-motion: reduce)').matches
        );
      }

      // FLIP (First-Last-Invert-Play): animate the mobile thumbnail set change so
      // the swap reads as a crossfade/morph instead of an instant reshuffle. The
      // strips (driven by --ir-active-index) are untouched — only thumb items animate.
      _withThumbFlip(apply) {
        if (!this._thumbItems.length || !this._thumbFlipEligible()) {
          apply();
          return;
        }

        const opts = { duration: 240, easing: 'ease' };
        const firstRects = new Map();
        this._thumbItems.forEach((item) => {
          const rect = item.getBoundingClientRect();
          if (rect.width > 0) firstRects.set(item, rect);
        });

        apply();

        this._thumbItems.forEach((item) => {
          const oldRect = firstRects.get(item);
          const newRect = item.getBoundingClientRect();
          const nowVisible = newRect.width > 0;

          if (oldRect && nowVisible) {
            const dx = oldRect.left - newRect.left;
            const dy = oldRect.top - newRect.top;
            if (dx || dy) {
              item.animate(
                [{ transform: `translate(${dx}px, ${dy}px)` }, { transform: 'none' }],
                opts
              );
            }
          } else if (!oldRect && nowVisible) {
            item.animate([{ opacity: 0 }, { opacity: 1 }], opts);
          } else if (oldRect && !nowVisible) {
            this._ghostFadeOut(item, oldRect);
          }
        });
      }

      // The now-active thumb is already display:none; render a fixed-position clone
      // over its old spot and fade it out so the outgoing/incoming thumbs overlap.
      _ghostFadeOut(item, rect) {
        const ghost = item.cloneNode(true);
        ghost.removeAttribute('role');
        ghost.style.cssText = `position:fixed;left:${rect.left}px;top:${rect.top}px;width:${rect.width}px;height:${rect.height}px;margin:0;list-style:none;pointer-events:none;z-index:5;`;
        document.body.appendChild(ghost);
        const anim = ghost.animate([{ opacity: 1 }, { opacity: 0 }], { duration: 240, easing: 'ease' });
        anim.finished.catch(() => {}).finally(() => ghost.remove());
      }

      _refresh({ announce, previousIndex } = {}) {
        const i = this._activeIndex;
        this.style.setProperty('--ir-active-index', String(i));

        this._thumbs.forEach((thumb, idx) => {
          const isActive = idx === i;
          thumb.setAttribute('aria-selected', isActive ? 'true' : 'false');
          thumb.setAttribute('tabindex', isActive ? '0' : '-1');
        });

        const applyHidden = (nodes) => {
          nodes.forEach((node, idx) => {
            if (idx === i) {
              node.removeAttribute('aria-hidden');
            } else {
              node.setAttribute('aria-hidden', 'true');
            }
          });
        };
        applyHidden(this._mediaSlides);
        applyHidden(this._contentSlides);

        if (this._prevBtn) this._prevBtn.disabled = i <= 0;
        if (this._nextBtn) this._nextBtn.disabled = i >= this._total - 1;

        this._coordinateVideos(previousIndex);

        if (announce && this._liveRegion) {
          const title = this._readSlideTitle(i);
          const message = this._positionTmpl
            .replace('__index__', String(i + 1))
            .replace('__total__', String(this._total))
            .replace('__title__', title);
          this._liveRegion.textContent = '';
          window.requestAnimationFrame(() => {
            this._liveRegion.textContent = message;
          });
        }
      }

      _readSlideTitle(index) {
        const titleEl = this._contentSlides[index]?.querySelector('.interactive-reveal__title');
        return titleEl ? titleEl.textContent.trim() : '';
      }

      _coordinateVideos(previousIndex) {
        if (typeof previousIndex === 'number' && previousIndex !== this._activeIndex) {
          const prevVideo = this._mediaSlides[previousIndex]?.querySelector('video');
          if (prevVideo && !prevVideo.paused) {
            try { prevVideo.pause(); } catch (err) { /* noop */ }
          }
        }
        const activeVideo = this._mediaSlides[this._activeIndex]?.querySelector('video');
        if (activeVideo) {
          const playPromise = activeVideo.play();
          if (playPromise && typeof playPromise.catch === 'function') {
            playPromise.catch(() => { /* autoplay blocked; ignore */ });
          }
        }
      }

      _dispatchAnalytics(index) {
        const detail = {
          sectionId: this.dataset.sectionId,
          slideIndex: index,
          slideTitle: this._readSlideTitle(index),
          mediaType: this._mediaSlides[index]?.dataset.mediaType || null,
        };

        this.dispatchEvent(
          new CustomEvent('interactive-reveal:slide-changed', {
            bubbles: true,
            composed: true,
            detail,
          })
        );

        if (!this._analyticsEnabled) return;
        if (window.dataLayer && typeof window.dataLayer.push === 'function') {
          window.dataLayer.push({
            event: 'interactive_reveal_slide_change',
            ...detail,
          });
        }
      }

      // Mobile horizontal-swipe on the media + text regions (thumbs / arrows /
      // CTA are excluded so their taps still fire). Direction-locked so a
      // vertical drag lets the description scroll natively.
      _bindSwipe() {
        const { signal } = this._abortController;
        this._swipeMq = window.matchMedia('(max-width: 989px)');
        this._reducedMotionMq = window.matchMedia('(prefers-reduced-motion: reduce)');
        this._swipeState = null;
        this._suppressNextClick = false;
        this._tapThreshold = 8;
        this._axisLockThreshold = 10;
        this._verticalDominanceRatio = 1.25;
        this._commitMinPx = 40;
        this._commitRatio = 0.2;

        this.addEventListener('pointerdown', (e) => this._onPointerDown(e), { signal });
        this.addEventListener('pointermove', (e) => this._onPointerMove(e), { signal });
        this.addEventListener('pointerup', (e) => this._onPointerUp(e), { signal });
        this.addEventListener('pointercancel', (e) => this._onPointerCancel(e), { signal });
        this.addEventListener('click', (e) => this._onClickCapture(e), { signal, capture: true });
      }

      _onPointerDown(e) {
        if (!this._swipeMq.matches) return;
        if (e.pointerType === 'mouse' && e.button !== 0) return;
        if (e.target.closest('[data-ir-thumbs], .interactive-reveal__arrows, .interactive-reveal__cta')) return;

        this._swipeState = {
          id: e.pointerId,
          startX: e.clientX,
          startY: e.clientY,
          axis: null,
          dragging: false,
        };
      }

      _onPointerMove(e) {
        const s = this._swipeState;
        if (!s || e.pointerId !== s.id) return;
        const dx = e.clientX - s.startX;
        const dy = e.clientY - s.startY;

        if (!s.axis) {
          const absDx = Math.abs(dx);
          const absDy = Math.abs(dy);
          if (absDx < this._axisLockThreshold && absDy < this._axisLockThreshold) return;
          if (absDy > absDx * this._verticalDominanceRatio) {
            // Vertical intent — release so native scroll owns the gesture.
            this._swipeState = null;
            return;
          }
          s.axis = 'x';
          s.dragging = true;
          try { this.setPointerCapture(e.pointerId); } catch (err) { /* noop */ }
          this.toggleAttribute('data-ir-dragging', true);
          // Pause any playing video on the current slide so audio/frame don't fight the scrub.
          const activeVideo = this._mediaSlides[this._activeIndex]?.querySelector('video');
          if (activeVideo && !activeVideo.paused) {
            try { activeVideo.pause(); } catch (err) { /* noop */ }
          }
        }

        if (s.axis !== 'x' || this._reducedMotionMq.matches) return;

        // Rubber-band at the ends so users feel the boundary.
        let offset = dx;
        if ((this._activeIndex === 0 && dx > 0) || (this._activeIndex === this._total - 1 && dx < 0)) {
          offset = dx * 0.35;
        }
        this.style.setProperty('--ir-drag-offset', `${offset}px`);
      }

      _onPointerUp(e) {
        const s = this._swipeState;
        if (!s || e.pointerId !== s.id) return;
        this._swipeState = null;

        if (!s.dragging) return;

        const dx = e.clientX - s.startX;
        const stageWidth = this._mediaFrame?.getBoundingClientRect().width || window.innerWidth;
        const commitThreshold = Math.min(this._commitMinPx, stageWidth * this._commitRatio);

        this.toggleAttribute('data-ir-dragging', false);
        this.style.removeProperty('--ir-drag-offset');

        let target = this._activeIndex;
        if (dx <= -commitThreshold && this._activeIndex < this._total - 1) {
          target = this._activeIndex + 1;
        } else if (dx >= commitThreshold && this._activeIndex > 0) {
          target = this._activeIndex - 1;
        }

        // Any real drag (dragging=true means we crossed the 8px threshold) should
        // swallow the trailing synthetic click — otherwise a swipe that ends over
        // a CTA would navigate.
        this._suppressNextClick = true;
        window.setTimeout(() => { this._suppressNextClick = false; }, 400);

        if (target !== this._activeIndex) this.setActive(target);
      }

      _onPointerCancel(e) {
        const s = this._swipeState;
        if (!s || e.pointerId !== s.id) return;
        this._swipeState = null;
        this.toggleAttribute('data-ir-dragging', false);
        this.style.removeProperty('--ir-drag-offset');
      }

      _onClickCapture(e) {
        if (!this._suppressNextClick) return;
        e.preventDefault();
        e.stopPropagation();
        this._suppressNextClick = false;
      }
    }
  );
}
